import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconDeviceTvOld,
  IconDeviceMobile,
  IconPlayerPlay,
  IconQuote,
  IconSchool,
  IconLayoutDashboard,
  IconChartHistogram,
  IconBuildingCommunity,
  IconUsersGroup,
  IconBuildingSkyscraper,
  IconStack2,
  IconReceipt2,
  IconCertificate,
  IconFileText,
  IconCreditCard,
  IconTrendingUp,
  IconCashBanknote,
  IconReportMoney,
  IconBook2,
  IconUserShield,
  IconHistory,
  IconLifebuoy,
  IconBell,
  IconSettings,
  IconBrandVimeo,
  IconAdjustments,
  IconChevronDown,
  IconGift,
  IconList,
  IconDiscount2,
  IconMailForward,
  IconLogout,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { Logo } from "@/components/ui/logo";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { cn } from "@/lib/utils/cn";

type ItemTone = "brand" | "warning" | "success" | "info" | "pink" | "orange";

interface NavChild {
  to: string;
  key: string;
  icon: TablerIcon;
  /** Marca como ativo só na rota exata (evita destacar o pai numa subrota). */
  end?: boolean;
}

interface NavItem {
  to?: string;
  key: string;
  icon: TablerIcon;
  tone: ItemTone;
  children?: NavChild[];
}

interface NavGroup {
  key: string;
  items: NavItem[];
}

const groups: NavGroup[] = [
  {
    key: "overview",
    items: [
      { to: "/dashboard", key: "dashboard", icon: IconLayoutDashboard, tone: "brand" },
      { to: "/metrics", key: "metrics", icon: IconChartHistogram, tone: "info" },
    ],
  },
  {
    key: "tenants",
    items: [
      { to: "/organizations", key: "organizations", icon: IconBuildingCommunity, tone: "brand" },
      { to: "/brokers", key: "brokers", icon: IconUsersGroup, tone: "success" },
      { to: "/developers", key: "developers", icon: IconBuildingSkyscraper, tone: "info" },
      {
        key: "referrals",
        icon: IconGift,
        tone: "warning",
        children: [
          { to: "/referrals", key: "referralsOverview", icon: IconList, end: true },
          { to: "/referrals/campaigns", key: "referralCampaigns", icon: IconDiscount2 },
        ],
      },
      { to: "/account-invites", key: "accountInvites", icon: IconMailForward, tone: "pink" },
    ],
  },
  {
    key: "billing",
    items: [
      {
        key: "billing",
        icon: IconReceipt2,
        tone: "success",
        children: [
          { to: "/billing", key: "billingDashboard", icon: IconLayoutDashboard },
          { to: "/subscriptions", key: "subscriptions", icon: IconCertificate },
          { to: "/price-adjustments", key: "priceAdjustments", icon: IconTrendingUp },
          { to: "/payments", key: "payments", icon: IconCreditCard },
        ],
      },
    ],
  },
  {
    key: "finance",
    items: [
      {
        key: "finance",
        icon: IconReportMoney,
        tone: "warning",
        children: [
          { to: "/finance", key: "financeDashboard", icon: IconLayoutDashboard },
          { to: "/finance/entries", key: "financeEntries", icon: IconReceipt2 },
          { to: "/finance/reports", key: "financeReports", icon: IconChartHistogram },
          { to: "/ledger", key: "ledger", icon: IconBook2 },
          { to: "/withdrawals", key: "withdrawals", icon: IconCashBanknote },
        ],
      },
    ],
  },
  {
    // Grupo proprio, e nao um item dentro de "Financeiro": afiliados e canal de
    // aquisicao — tem fila de pessoas, campanha e relatorio proprios. Aninhado ali,
    // o menu lia "Financeiro > Afiliados", que descreve errado o que a secao e.
    key: "affiliates",
    items: [
      {
        key: "affiliates",
        icon: IconUsersGroup,
        tone: "brand",
        children: [
          { to: "/affiliates", key: "affiliatesQueue", icon: IconUsersGroup },
          { to: "/affiliates/payouts", key: "affiliatePayouts", icon: IconCashBanknote },
          { to: "/affiliates/programs", key: "affiliatePrograms", icon: IconReportMoney },
          { to: "/affiliates/report", key: "affiliateReport", icon: IconChartHistogram },
        ],
      },
    ],
  },
  {
    key: "support",
    items: [{ to: "/support", key: "support", icon: IconLifebuoy, tone: "orange" }],
  },
  {
    key: "siteContent",
    items: [
      {
        key: "siteContent",
        icon: IconDeviceTvOld,
        tone: "pink",
        children: [
          { to: "/site-content/opening", key: "siteContentOpening", icon: IconPlayerPlay },
          { to: "/site-content/testimonials", key: "siteContentTestimonials", icon: IconQuote },
          { to: "/site-content/institutional", key: "siteContentInstitutional", icon: IconBuildingSkyscraper },
          { to: "/site-content/howto", key: "siteContentHowto", icon: IconSchool },
          { to: "/site-content/app", key: "siteContentApp", icon: IconDeviceMobile },
        ],
      },
    ],
  },
  {
    key: "system",
    items: [
      {
        key: "system",
        icon: IconSettings,
        tone: "info",
        children: [
          { to: "/plans", key: "plans", icon: IconStack2 },
          { to: "/legal", key: "legal", icon: IconFileText },
          { to: "/admin-users", key: "adminUsers", icon: IconUserShield },
          { to: "/notifications", key: "notifications", icon: IconBell },
          { to: "/audit", key: "audit", icon: IconHistory },
          { to: "/integrations/vimeo", key: "vimeoIntegration", icon: IconBrandVimeo },
          { to: "/settings", key: "settings", icon: IconAdjustments },
        ],
      },
    ],
  },
];

const toneActiveBg: Record<ItemTone, string> = {
  brand: "bg-gradient-to-r from-brand-500/25 to-brand-500/5",
  info: "bg-gradient-to-r from-info/25 to-info/5",
  success: "bg-gradient-to-r from-success/25 to-success/5",
  warning: "bg-gradient-to-r from-warning/30 to-warning/5",
  pink: "bg-gradient-to-r from-pink/25 to-pink/5",
  orange: "bg-gradient-to-r from-orange/25 to-orange/5",
};

const toneIcon: Record<ItemTone, string> = {
  brand: "bg-brand-500/15 text-brand-300",
  info: "bg-info/15 text-purple-300",
  success: "bg-success/15 text-emerald-300",
  warning: "bg-warning/15 text-amber-300",
  pink: "bg-pink/15 text-pink-300",
  orange: "bg-orange/15 text-orange-300",
};

const toneIconActive: Record<ItemTone, string> = {
  brand: "bg-brand-grad text-white shadow-glow",
  info: "bg-gradient-to-br from-info to-brand-500 text-white shadow-glow-purple",
  success: "bg-argon-grad-success text-white",
  warning: "bg-gradient-to-br from-warning to-orange text-white",
  pink: "bg-argon-grad-warm text-white shadow-glow-pink",
  orange: "bg-gradient-to-br from-orange to-pink text-white",
};

function LeafLink({ to, label, icon: Icon, tone, onItemClick }: {
  to: string;
  label: string;
  icon: TablerIcon;
  tone: ItemTone;
  onItemClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onItemClick}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-all",
          isActive
            ? cn(toneActiveBg[tone], "text-white shadow-inner-light")
            : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-white"
            />
          )}
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all",
              isActive ? toneIconActive[tone] : toneIcon[tone],
            )}
          >
            <Icon size={16} />
          </span>
          <span className="flex-1 truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function CollapsibleItem({ item, onItemClick }: { item: NavItem; onItemClick?: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const children = item.children ?? [];
  const childActive = children.some((c) =>
    c.end ? location.pathname === c.to : location.pathname.startsWith(c.to),
  );
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const Icon = item.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-all",
          childActive ? cn(toneActiveBg[item.tone], "text-white") : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
        )}
      >
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all",
            childActive ? toneIconActive[item.tone] : toneIcon[item.tone],
          )}
        >
          <Icon size={16} />
        </span>
        <span className="flex-1 truncate text-left">{t(`nav.items.${item.key}`)}</span>
        <IconChevronDown
          size={14}
          className={cn("shrink-0 text-white/40 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
          {children.map((c) => {
            const CIcon = c.icon;
            return (
              <li key={c.to}>
                <NavLink
                  to={c.to}
                  end={c.end}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12px] transition-all",
                      isActive
                        ? "bg-white/[0.07] font-medium text-white"
                        : "text-slate-400 hover:bg-white/[0.03] hover:text-white",
                    )
                  }
                >
                  <CIcon size={15} className="shrink-0" />
                  <span className="flex-1 truncate">{t(`nav.items.${c.key}`)}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function Sidebar({ onItemClick }: { onItemClick?: () => void }) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);

  return (
    <aside className="relative flex h-full w-[228px] flex-col overflow-hidden bg-sidebar-grad text-slate-200 shadow-[8px_0_24px_-12px_rgba(8,12,32,0.55)]">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
      />

      <div className="relative z-10 flex items-center justify-between gap-3 border-b border-white/5 px-5 py-3">
        <Logo invert />
        <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/60 backdrop-blur">
          Admin
        </span>
      </div>

      <nav className="scrollbar-none relative z-10 flex-1 overflow-y-auto px-3 py-3">
        {groups.map((group) => (
          <div key={group.key} className="mb-3 last:mb-0">
            {group.key !== "overview" &&
              group.key !== "system" &&
              group.key !== "billing" &&
              group.key !== "finance" &&
              group.key !== "support" && (
              <p className="px-2.5 pb-1 text-[9.5px] font-bold uppercase tracking-wider text-white/35">
                {t(`nav.groups.${group.key}`)}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.key}>
                  {item.children ? (
                    <CollapsibleItem item={item} onItemClick={onItemClick} />
                  ) : (
                    <LeafLink
                      to={item.to ?? "#"}
                      label={t(`nav.items.${item.key}`)}
                      icon={item.icon}
                      tone={item.tone}
                      onItemClick={onItemClick}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="relative z-10 border-t border-white/5 p-3">
        <button
          type="button"
          onClick={() => {
            onItemClick?.();
            logout();
          }}
          className="group flex w-full items-center gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-slate-300 transition-all hover:bg-white/[0.04] hover:text-white"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-slate-300 transition-all group-hover:bg-danger/20 group-hover:text-red-300">
            <IconLogout size={16} />
          </span>
          <span className="flex-1 truncate text-left">{t("actions.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
